/**
 * "Fissare gli appuntamenti": per ruolo, per chi si possono fissare appuntamenti e da chi parte
 * il campo "Nel calendario di". Regole pure, testate in __tests__/booking.test.ts.
 *
 * Vivono in `settings.calendar_settings.booking.rules`. Chi non ha un ruolo con una regola
 * lavora come prima: tutte le persone nell'elenco e il valore di partenza di sempre.
 */
import type { CalendarPerson } from './sharing';

/** Da chi parte "Nel calendario di": nessuno (si sceglie per forza), il proprietario del lead, chi fissa. */
export type BookingStart = 'none' | 'lead_owner' | 'booker';

export interface BookingRule {
  /** ruolo di chi fissa */
  role_id: string;
  /** ruoli delle persone per cui puo' fissare */
  target_role_ids: string[];
  /** puo' fissare anche nel proprio calendario (default no) */
  include_self?: boolean;
  start: BookingStart;
}

export interface BookingSettings { rules?: BookingRule[] }

export const START_LABELS: Record<BookingStart, string> = {
  none: 'Nessuno, si sceglie ogni volta',
  lead_owner: 'Proprietario del lead',
  booker: 'Chi fissa',
};

export const START_HELP: Record<BookingStart, string> = {
  none: 'Il campo parte vuoto e non si salva finché non si sceglie la persona.',
  lead_owner: 'Parte dal proprietario del lead, se è fra le persone ammesse; altrimenti vuoto.',
  booker: 'Parte da chi sta fissando, se può fissare anche per sé; altrimenti vuoto.',
};

/** Tipi che sono appuntamenti: la regola vale solo per questi (una chiamata da fare resta libera). */
export const APPOINTMENT_TYPES = ['videochiamata', 'meeting', 'appuntamento', 'sopralluogo'] as const;

export function isAppointmentType(type: string | null | undefined): boolean {
  if (!type) return false;
  return (APPOINTMENT_TYPES as readonly string[]).includes(type) || type.startsWith('alfadocs_');
}

export interface BookingResolution {
  /** persone che si possono scegliere */
  allowedIds: Set<string>;
  start: BookingStart;
  /** valore di partenza del campo (null = vuoto, da scegliere) */
  startId: string | null;
}

const START_RANK: Record<BookingStart, number> = { none: 3, lead_owner: 2, booker: 1 };

/** Regole valide (ruolo, almeno un ruolo destinatario o "anche per sé", partenza scelta). */
export function cleanRules(rules: readonly BookingRule[] | null | undefined): BookingRule[] {
  return (rules || []).filter(r => r && r.role_id && (r.start === 'none' || r.start === 'lead_owner' || r.start === 'booker')
    && ((r.target_role_ids || []).length > 0 || r.include_self));
}

/**
 * Cosa vale per chi fissa. null = nessuna regola per i suoi ruoli o tipo che non e' un appuntamento:
 * comportamento di oggi. Con piu' ruoli: unione delle persone; per la partenza vince la piu' prudente
 * (nessuno, poi proprietario del lead, poi chi fissa).
 */
export function resolveBooking(params: {
  meId: string | null;
  people: readonly CalendarPerson[];
  rules: readonly BookingRule[] | null | undefined;
  type: string | null | undefined;
  leadOwnerId?: string | null;
}): BookingResolution | null {
  const { meId, people, type, leadOwnerId } = params;
  if (!meId || !isAppointmentType(type)) return null;
  const me = people.find(p => p.id === meId);
  if (!me) return null;
  const mine = cleanRules(params.rules).filter(r => me.roleIds.includes(r.role_id));
  if (!mine.length) return null;
  const targetRoles = new Set(mine.flatMap(r => r.target_role_ids || []));
  const allowedIds = new Set(people.filter(p => p.roleIds.some(id => targetRoles.has(id))).map(p => p.id));
  if (mine.some(r => r.include_self)) allowedIds.add(meId);
  else allowedIds.delete(meId);
  const start = mine.map(r => r.start).sort((a, b) => START_RANK[b] - START_RANK[a])[0];
  const candidate = start === 'lead_owner' ? leadOwnerId || null : start === 'booker' ? meId : null;
  return { allowedIds, start, startId: candidate && allowedIds.has(candidate) ? candidate : null };
}

/** Messaggio che blocca il salvataggio, o null se la persona scelta va bene. */
export function bookingError(res: BookingResolution | null, assignedTo: string | null | undefined): string | null {
  if (!res) return null;
  if (!assignedTo) return "Scegli nel calendario di chi va l'appuntamento";
  if (!res.allowedIds.has(assignedTo)) return 'Questa persona non è fra quelle per cui puoi fissare appuntamenti';
  return null;
}

/** Elenco delle persone da proporre: con una regola solo quelle ammesse (piu' quella gia' salvata, in modifica). */
export function filterBookingOptions<T extends { id: string }>(users: readonly T[], res: BookingResolution | null, keepId?: string | null): T[] {
  if (!res) return [...users];
  return users.filter(u => res.allowedIds.has(u.id) || (!!keepId && u.id === keepId));
}

/** Messaggio di errore per una regola in modifica nelle impostazioni, o null se si puo' salvare. */
export function ruleDraftError(r: Partial<BookingRule>): string | null {
  if (!r.role_id) return 'Scegli il ruolo di chi fissa';
  if (!(r.target_role_ids || []).length && !r.include_self) return 'Scegli almeno un ruolo per cui fissa';
  if (!r.start) return 'Scegli da dove parte il campo';
  return null;
}

/** Tipo usato per sapere se chi compila ha una regola, prima ancora di scegliere il tipo. */
export const BOOKING_PROBE_TYPE = 'videochiamata';

/**
 * Tipo di partenza di una NUOVA attivita'. Dalla scheda di un lead, chi ha una regola "Fissare gli
 * appuntamenti" (operatrici che fissano per i consulenti) parte da Videochiamata; altrimenti il default di sempre.
 */
export function initialActivityType(params: { defaultType: string; hasBookingRule: boolean; fromLead: boolean }): string {
  return params.hasBookingRule && params.fromLead ? BOOKING_PROBE_TYPE : params.defaultType;
}

/** Testo del pulsante sotto "Nel calendario di": consulenti con una regola, colleghi se ne vedo altri, altrimenti il mio. */
export function calendarsButtonLabel(params: { hasBookingRule: boolean; canSeeTeam: boolean }): string {
  if (!params.canSeeTeam) return 'Vedi il calendario e scegli lo spazio libero';
  return params.hasBookingRule
    ? 'Vedi i calendari dei consulenti e scegli lo spazio libero'
    : 'Vedi i calendari dei colleghi e scegli lo spazio libero';
}
